import { useEffect, useState, useCallback } from "react";
import { endpoints, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import {
    ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { Plus, CircleNotch, Trash, Drop, Thermometer } from "@phosphor-icons/react";

const inputCls = "w-full h-9 px-3 rounded-[4px] border border-[var(--rom-border)] text-sm focus:border-[var(--rom-orange)] focus:outline-none bg-white";
const ZONE_COLORS = ["#0E1A24", "#FF6A13", "#00853F", "#7c3aed", "#0891b2", "#be123c"];

function zoneColor(zone, zones) {
    const i = Math.max(0, zones.indexOf(zone));
    return ZONE_COLORS[i % ZONE_COLORS.length];
}

const READING_TYPE_LABEL = { psychrometric: "Psychrometric (Web)", material_moisture: "Material moisture (Mobile)" };

const emptyForm = () => ({
    date: new Date().toISOString().slice(0, 10), zone: "", temperature: "", relative_humidity: "",
    moisture_content: "", equipment_id: "", notes: "",
});

export default function MoistureLogTab({ project }) {
    const { user } = useAuth();
    const canDelete = ["admin", "manager"].includes(user?.role);
    const [trend, setTrend] = useState(null);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState(emptyForm());
    const [submitting, setSubmitting] = useState(false);
    const [err, setErr] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        try { const { data } = await endpoints.moistureTrend(project.id); setTrend(data); }
        catch { /* ignore */ }
        finally { setLoading(false); }
    }, [project.id]);
    useEffect(() => { load(); }, [load]);

    const readings = trend?.readings || [];
    const zones = trend?.zones || [];
    const chartSeries = trend?.chartSeries || trend?.chart_series || [];
    const chartData = trend?.chartData || trend?.chart_data || [];
    const equipmentOpts = project.equipment_hire || [];


    async function submit() {
        const hasMeasurement = form.temperature !== "" || form.relative_humidity !== "" || form.moisture_content !== "";
        if (!hasMeasurement) { setErr("Enter at least one reading (temperature, RH% or moisture content/GPP)"); return; }
        setSubmitting(true); setErr("");
        try {
            const equip = equipmentOpts.find((e) => e.id === form.equipment_id);
            await endpoints.createMoistureReading({
                project_id: project.id, date: form.date, zone: form.zone,
                temperature: form.temperature === "" ? null : Number(form.temperature),
                relative_humidity: form.relative_humidity === "" ? null : Number(form.relative_humidity),
                moisture_content: form.moisture_content === "" ? null : Number(form.moisture_content),
                equipment_id: form.equipment_id || null, equipment_label: equip?.item || "",
                notes: form.notes,
            });
            setForm((f) => ({ ...emptyForm(), zone: f.zone })); // keep zone selected for rapid multi-zone entry
            await load();
        } catch (e) { setErr(formatApiError(e)); }
        finally { setSubmitting(false); }
    }

    async function remove(id) {
        try { await endpoints.deleteMoistureReading(id); await load(); }
        catch (e) { alert(formatApiError(e)); }
    }

    return (
        <div className="space-y-4" data-testid="moisture-log-tab">
            <div className="rom-card">
                <div className="rom-card-head">
                    <h3 className="flex items-center gap-2"><Drop size={16} weight="bold" className="text-[var(--rom-orange)]" /> Drying progress — psychrometric &amp; material readings</h3>
                    <span className="text-xs text-[var(--rom-text-3)]">{trend?.total_readings || 0} readings logged</span>
                </div>
                <div className="rom-card-body">
                    {loading ? (
                        <div className="text-center py-10 text-[var(--rom-text-3)] text-sm">Loading…</div>
                    ) : chartData.length < 2 ? (
                        <div className="text-center py-10 text-[var(--rom-text-3)] text-sm" data-testid="moisture-chart-empty">
                            Log at least two days of readings to see the drying trend chart.
                        </div>
                    ) : (
                        <ResponsiveContainer width="100%" height={260}>
                            <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#eef1f4" vertical={false} />
                                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} width={36} unit="%" />
                                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }} />
                                <Legend wrapperStyle={{ fontSize: 11 }} />
                                {chartSeries.map((s) => (
                                    <Line key={s.key} type="monotone" dataKey={s.key} name={s.label}
                                        stroke={zoneColor(s.zone, zones)} strokeWidth={2} dot={{ r: 3 }}
                                        strokeDasharray={s.reading_type === "material_moisture" ? "5 3" : undefined}
                                        connectNulls data-testid={`moisture-series-${s.key}`} />
                                ))}
                            </LineChart>
                        </ResponsiveContainer>
                    )}
                    <div className="text-[11px] text-[var(--rom-text-3)] mt-2 flex items-center gap-4 flex-wrap">
                        <span>Trending down over time indicates drying progress. Include this chart in the closeout package.</span>
                        <span className="flex items-center gap-1.5"><span className="inline-block w-4 h-0 border-t-2 border-slate-500" /> Psychrometric (Web)</span>
                        <span className="flex items-center gap-1.5"><span className="inline-block w-4 h-0 border-t-2 border-dashed border-slate-500" /> Material moisture (Mobile)</span>
                    </div>
                </div>
            </div>

            {trend?.latest_by_zone?.length > 0 && (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="moisture-latest-grid">
                    {trend.latest_by_zone.map((r) => (
                        <div key={`${r.zone}__${r.reading_type}`} className="rom-card">
                            <div className="rom-card-body py-3">
                                <div className="text-[11px] uppercase tracking-wider text-[var(--rom-text-2)] font-semibold truncate">{r.zone}</div>
                                <div className="text-[10px] text-[var(--rom-text-3)] mb-0.5">{READING_TYPE_LABEL[r.reading_type] || r.reading_type}</div>
                                <div className="flex items-baseline gap-3 mt-1">
                                    {r.reading_type === "psychrometric" ? (
                                        <>
                                            {r.relative_humidity != null && <span className="font-display font-black text-xl">{r.relative_humidity}<span className="text-xs font-normal">% RH</span></span>}
                                            {r.temperature != null && <span className="text-xs text-[var(--rom-text-2)] flex items-center gap-0.5"><Thermometer size={11} weight="bold" /> {r.temperature}°F</span>}
                                        </>
                                    ) : (
                                        <>
                                            {r.value != null && <span className="font-display font-black text-xl">{r.value}<span className="text-xs font-normal">{r.unit || "%"}</span></span>}
                                            {r.material && <span className="text-xs text-[var(--rom-text-2)] capitalize">{r.material}</span>}
                                        </>
                                    )}
                                </div>
                                <div className="text-[10px] text-[var(--rom-text-3)] mt-0.5">as of {r.date}</div>
                            </div>
                        </div>
                    ))}
                </div>
            )}


            <div className="rom-card">
                <div className="rom-card-head"><h3>Log a reading</h3><span className="text-xs text-[var(--rom-text-3)]">Any crew member can submit</span></div>
                <div className="rom-card-body space-y-3">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                        <div>
                            <label className="block text-xs font-semibold text-[var(--rom-text-2)] mb-1">Date *</label>
                            <input data-testid="ml-date" type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} className={inputCls} />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-[var(--rom-text-2)] mb-1">Zone / room</label>
                            <input data-testid="ml-zone" list="ml-zone-list" value={form.zone} onChange={(e) => setForm((f) => ({ ...f, zone: e.target.value }))} placeholder="e.g. Living Room" className={inputCls} />
                            <datalist id="ml-zone-list">{zones.map((z) => <option key={z} value={z === "Whole site" ? "" : z} />)}</datalist>
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-[var(--rom-text-2)] mb-1">Temp (°F)</label>
                            <input data-testid="ml-temperature" type="number" value={form.temperature} onChange={(e) => setForm((f) => ({ ...f, temperature: e.target.value }))} className={inputCls} />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-[var(--rom-text-2)] mb-1">Relative humidity (%)</label>
                            <input data-testid="ml-rh" type="number" value={form.relative_humidity} onChange={(e) => setForm((f) => ({ ...f, relative_humidity: e.target.value }))} className={inputCls} />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                        <div>
                            <label className="block text-xs font-semibold text-[var(--rom-text-2)] mb-1">Moisture content / GPP</label>
                            <input data-testid="ml-moisture" type="number" value={form.moisture_content} onChange={(e) => setForm((f) => ({ ...f, moisture_content: e.target.value }))} className={inputCls} />
                        </div>
                        {equipmentOpts.length > 0 && (
                            <div className="col-span-2">
                                <label className="block text-xs font-semibold text-[var(--rom-text-2)] mb-1">Equipment (optional)</label>
                                <select data-testid="ml-equipment" value={form.equipment_id} onChange={(e) => setForm((f) => ({ ...f, equipment_id: e.target.value }))} className="select">
                                    <option value="">— None —</option>
                                    {equipmentOpts.map((e) => <option key={e.id} value={e.id}>{e.item}{e.vendor ? ` · ${e.vendor}` : ""}</option>)}
                                </select>
                            </div>
                        )}
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-[var(--rom-text-2)] mb-1">Notes</label>
                        <textarea data-testid="ml-notes" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} rows={2}
                            className="w-full px-3 py-2 rounded-[4px] border border-[var(--rom-border)] text-sm focus:border-[var(--rom-orange)] focus:outline-none resize-none" />
                    </div>
                    {err && <div className="text-xs text-red-600" data-testid="ml-error">{err}</div>}
                    <button data-testid="ml-submit" onClick={submit} disabled={submitting} className="btn btn-primary">
                        {submitting ? <CircleNotch size={14} className="animate-spin" /> : <Plus size={14} weight="bold" />} Log reading
                    </button>
                </div>
            </div>

            <div className="rom-card">
                <div className="rom-card-head"><h3>Reading history</h3></div>
                <div className="overflow-x-auto">
                    <table className="rom-table">
                        <thead><tr><th>Date</th><th>Type</th><th>Zone</th><th className="numeric">Temp</th><th className="numeric">RH%</th><th className="numeric">Moisture/GPP/Material</th><th>Equipment / material</th><th>Recorded by</th>{canDelete && <th></th>}</tr></thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={canDelete ? 9 : 8} className="text-center py-8 text-[var(--rom-text-3)]">Loading…</td></tr>
                            ) : readings.length === 0 ? (
                                <tr><td colSpan={canDelete ? 9 : 8} className="text-center py-8 text-[var(--rom-text-3)]" data-testid="ml-empty">No readings logged yet.</td></tr>
                            ) : [...readings].reverse().map((r) => (
                                <tr key={`${r.reading_type}-${r.id}`} data-testid={`ml-row-${r.id}`}>
                                    <td className="font-mono text-xs">{r.date}</td>
                                    <td><span className={`chip text-[10px] ${r.reading_type === "psychrometric" ? "chip-navy" : "chip-orange"}`}>{r.reading_type === "psychrometric" ? "Psychrometric" : "Material · Mobile"}</span></td>
                                    <td>{r.zone || <span className="text-[var(--rom-text-3)]">Whole site</span>}</td>
                                    <td className="numeric">{r.temperature != null ? `${r.temperature}°F` : "—"}</td>
                                    <td className="numeric">{r.relative_humidity != null ? `${r.relative_humidity}%` : "—"}</td>
                                    <td className="numeric">{r.reading_type === "psychrometric" ? (r.moisture_content ?? "—") : `${r.value ?? "—"}${r.unit || ""}`}</td>
                                    <td className="text-xs text-[var(--rom-text-2)]">{r.reading_type === "psychrometric" ? (r.equipment_label || "—") : (r.material || "—")}</td>
                                    <td className="text-[var(--rom-text-2)]">{r.recorded_by}</td>
                                    {canDelete && (
                                        <td>
                                            {r.source === "field_gateway" ? (
                                                <span className="text-[10px] text-[var(--rom-text-3)]" data-testid={`ml-readonly-${r.id}`}>via mobile</span>
                                            ) : (
                                                <button onClick={() => remove(r.id)} data-testid={`ml-delete-${r.id}`} className="p-1 rounded text-[var(--rom-text-2)] hover:bg-red-50 hover:text-[var(--rom-danger)]">
                                                    <Trash size={13} weight="bold" />
                                                </button>
                                            )}
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
